import { z } from "zod";
import { isCardNumberUsername } from "./participant-accounts";
import { CLIENT_APPS, type ClientApp, ROLE_CODES, type RoleCode } from "./auth";
import { keysetQuery, paginationQuery, searchParam } from "./pagination";
import type { UserGroupRef } from "./user-groups";

/**
 * `/users` — the admin user directory. Wire shapes only (snake_case), as the
 * API's `ok()` emits them after `snakeCaseKeys()`; see CLAUDE.md § Naming.
 */

export const USER_STATUSES = ["ACTIVE", "INACTIVE", "SUSPENDED"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const USER_SORT_KEYS = ["name", "username", "created_at"] as const;
export type UserSortKey = (typeof USER_SORT_KEYS)[number];

/**
 * Query string for `GET /users`, shared by the API validator's shape and the
 * web app's URL search params so both sides agree on names and caps.
 */
export const listUsersQuery = paginationQuery.extend({
	role: z.enum(ROLE_CODES).optional(),
	status: z.enum(USER_STATUSES).optional(),
	search: searchParam.optional(),
	/** Members of one user group. */
	user_group_id: z.string().uuid().optional(),
	sort: z.enum(USER_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuery>;

/** One row of `GET /users` as seen by SUPER_ADMIN / ADMIN (the only roles allowed to list). */
export interface UserListItem {
	id: string;
	username: string;
	name: string;
	email: string | null;
	phone: string | null;
	status: UserStatus;
	must_change_password: boolean;
	/** ISO-8601 UTC; render in MYT. */
	last_login_at: string | null;
	created_at: string;
	updated_at: string;
	roles: Array<{ code: RoleCode; name: string; rank: number }>;
	mfa_enabled: boolean;
	/** The login's user group (one per login), or null. It applies only while the active role equals its `role_code`. */
	user_group: UserGroupRef | null;
}

// ── Scope references ────────────────────────────────────────────────────────
// Where a scoped role (see SCOPED_ROLES) may see and write accreditation data
// is decided by its user group's scope rows (./user-groups `GroupScope`). These
// are the reference shapes those rows embed.

export interface ScopeRef {
	id: string;
	name: string;
}
export interface ScopeCodedRef extends ScopeRef {
	code: string;
}

/**
 * `GET /users/:id` — the list row. What a scoped login may do lives on its
 * group (`GET /user-groups/:id`), not on the account.
 */
export type UserDetail = UserListItem;

/** `GET /users/scope-options` — the pick-lists a group scope row is built from, in one call. */
export interface ScopeOptions {
	responsible_organizations: Array<{ id: string; name: string; type: string | null }>;
	main_functions: Array<{ id: string; code: string; name: string }>;
	sub_agencies: Array<{ id: string; name: string }>;
	sub_functions: Array<{ id: string; main_function_id: string; code: string; name: string }>;
	games: Array<{ id: string; code: string; name: string }>;
}

// ── Write requests ──────────────────────────────────────────────────────────
// Declared in snake_case to mirror `apps/api/src/schemas/user.schema.ts`, so
// the form validates with the same rules the server will apply.

/**
 * Every human-readable validation message the user form schemas emit, so a
 * frontend can hand in translated copy (`apps/gms/src/lib/i18n/use-form-schemas.ts`)
 * while the API and the default exports keep the English wire copy verbatim.
 */
export interface UserFormMessages {
	usernameMin: string;
	usernameFormat: string;
	usernameNumeric: string;
	emailInvalid: string;
	phoneMin: string;
	passwordMin: string;
	nameRequired: string;
	rolesRequired: string;
	confirmPasswordRequired: string;
	newPasswordRequired: string;
}

/** The exact strings these schemas have always emitted — the English default. */
export const enUserFormMessages: UserFormMessages = {
	usernameMin: "At least 3 characters",
	usernameFormat: "No '@' or spaces",
	usernameNumeric: "Cannot be only digits: those usernames belong to card numbers",
	emailInvalid: "Enter a valid email",
	phoneMin: "At least 3 characters",
	passwordMin: "At least 8 characters",
	nameRequired: "Enter a name",
	rolesRequired: "Pick at least one role",
	confirmPasswordRequired: "Enter your own password to confirm",
	newPasswordRequired: "Type the new password, or choose Generate",
};

/** Must not contain '@' — that is what keeps a login identifier unambiguous between username and email. */
export const makeUsernameField = (m: UserFormMessages = enUserFormMessages) =>
	z
		.string()
		.trim()
		.min(3, m.usernameMin)
		.max(64)
		.regex(/^[^@\s]+$/, m.usernameFormat);
export const usernameField = makeUsernameField();

/**
 * A NEW staff login's username: also never all digits — those are the card
 * numbers participant logins sign in with (0089), so the two can never collide.
 */
export const makeStaffUsernameField = (m: UserFormMessages = enUserFormMessages) =>
	makeUsernameField(m).refine((v) => !isCardNumberUsername(v), m.usernameNumeric);
export const staffUsernameField = makeStaffUsernameField();

/** Optional text inputs bind as `""`; `toCreateUserBody` / `toUpdateUserBody` drop or null them. */
const optionalEmail = (m: UserFormMessages) => z.string().trim().email(m.emailInvalid).max(320).or(z.literal(""));
const optionalPhone = (m: UserFormMessages) => z.string().trim().min(3, m.phoneMin).max(30).or(z.literal(""));
const optionalPassword = (m: UserFormMessages) => z.string().min(8, m.passwordMin).max(200).or(z.literal(""));

export function makeCreateUserForm(m: UserFormMessages = enUserFormMessages) {
	return z.object({
		name: z.string().trim().min(1, m.nameRequired).max(200),
		username: makeStaffUsernameField(m),
		email: optionalEmail(m),
		phone: optionalPhone(m),
		/** Blank → the server generates a phone-friendly temporary password and returns it once. */
		password: optionalPassword(m),
		roles: z.array(z.enum(ROLE_CODES)).min(1, m.rolesRequired),
		must_change_password: z.boolean(),
	});
}
export const createUserForm = makeCreateUserForm();
export type CreateUserForm = z.infer<typeof createUserForm>;

export function makeUpdateUserForm(m: UserFormMessages = enUserFormMessages) {
	return z.object({
		name: z.string().trim().min(1, m.nameRequired).max(200),
		username: makeUsernameField(m),
		email: optionalEmail(m),
		phone: optionalPhone(m),
		roles: z.array(z.enum(ROLE_CODES)).min(1, m.rolesRequired),
		status: z.enum(USER_STATUSES),
	});
}
export const updateUserForm = makeUpdateUserForm();
export type UpdateUserForm = z.infer<typeof updateUserForm>;

/**
 * `POST /users/:id/reset-password`. The admin re-proves it is them with
 * `confirm_password` (their OWN password) — a misclick must not sign someone
 * out everywhere. `mode: "generate"` sends no `new_password`.
 */
export function makeResetPasswordForm(m: UserFormMessages = enUserFormMessages) {
	return z
		.object({
			mode: z.enum(["generate", "set"]),
			new_password: optionalPassword(m),
			confirm_password: z.string().min(1, m.confirmPasswordRequired).max(200),
		})
		.refine((v) => v.mode === "generate" || v.new_password.length > 0, {
			message: m.newPasswordRequired,
			path: ["new_password"],
		});
}
export const resetPasswordForm = makeResetPasswordForm();
export type ResetPasswordForm = z.infer<typeof resetPasswordForm>;

export interface ResetPasswordBody {
	new_password?: string;
	confirm_password: string;
}

export const toResetPasswordBody = (f: ResetPasswordForm): ResetPasswordBody => ({
	...(f.mode === "set" ? { new_password: f.new_password } : {}),
	confirm_password: f.confirm_password,
});

/** Body of `POST /users`. */
export interface CreateUserBody {
	username: string;
	name: string;
	email?: string;
	phone?: string;
	password?: string;
	roles: RoleCode[];
	must_change_password?: boolean;
}

/** Body of `PATCH /users/:id` — profile fields only; roles / status / password have their own endpoints. */
export interface UpdateUserBody {
	username?: string;
	name?: string;
	email?: string | null;
	phone?: string | null;
}

export const toCreateUserBody = (f: CreateUserForm): CreateUserBody => ({
	username: f.username,
	name: f.name,
	...(f.email ? { email: f.email } : {}),
	...(f.phone ? { phone: f.phone } : {}),
	...(f.password ? { password: f.password } : {}),
	roles: f.roles,
	must_change_password: f.must_change_password,
});

// ── Write responses ─────────────────────────────────────────────────────────

/** `POST /users` — `temporary_password` is present only when no password was supplied, and only once. */
export interface CreatedUser extends UserListItem {
	temporary_password?: string;
}

/** `POST /users/:id/reset-password`. */
export interface ResetPasswordResult {
	generated: boolean;
	temporary_password?: string;
}

// ── Bulk actions (SUPER_ADMIN) ──────────────────────────────────────────────
// One request per action, not one per selected user. Each is confirmed with
// the admin's OWN password; a wrong one is 403 CONFIRMATION_PASSWORD_INVALID
// and nothing changes. The caller's own id in `ids` is 403 CANNOT_TARGET_SELF.

/** Most users one bulk request may name (the API refuses more with 422). */
export const BULK_USERS_MAX = 100;

/** Body of `POST /users/bulk/status`. `ids`: 1–100 distinct user ids. */
export interface BulkStatusBody {
	ids: string[];
	status: UserStatus;
	confirm_password: string;
}

/** `POST /users/bulk/status`. */
export interface BulkStatusResult {
	/** Accounts whose status actually changed. Unknown ids and accounts already in that status are absent. */
	updated_ids: string[];
	/** Live sessions ended (INACTIVE / SUSPENDED only; always 0 when re-activating). */
	sessions_revoked: number;
}

/** Body of `POST /users/bulk/revoke-sessions` — "sign out everywhere" for each selected user. */
export interface BulkRevokeSessionsBody {
	ids: string[];
	confirm_password: string;
}

/** `POST /users/bulk/revoke-sessions`. */
export interface BulkRevokeSessionsResult {
	/** Selected accounts that exist — every one is now signed out everywhere, live session or not. */
	user_ids: string[];
	/** Live sessions ended. */
	sessions_revoked: number;
}

/** One row of `GET /roles`. */
export interface RoleItem {
	code: RoleCode;
	name: string;
	description: string | null;
	rank: number;
	is_assignable: boolean;
	mfa_required: boolean;
	updated_at: string;
}

// ── Account detail: activity, sign-ins, sessions ────────────────────────────

export type AuditAction = "INSERT" | "UPDATE" | "DELETE";
export type AuditActorType = "USER" | "AI_AGENT" | "SYSTEM" | "MIGRATION" | "CUSTOMER";

/** One row of `GET /users/:id/activity`. Secret values arrive as "[REDACTED]". */
export interface AuditEntry {
	id: string;
	occurred_at: string;
	action: AuditAction;
	entity: { table: string; id: string; username: string | null; name: string | null };
	actor: { type: AuditActorType; user_id: string | null; username: string | null; name: string | null; label: string | null };
	reason: string | null;
	changed_fields: string[];
	changes: Record<string, { from: unknown; to: unknown }>;
	/** INSERT / DELETE only: a few identifying columns of the row. */
	snapshot: Record<string, unknown> | null;
	ip_address: string | null;
	user_agent: string | null;
	request_id: string | null;
}

/** Keyset page: pass `next_cursor` back as `?cursor=` for the next page; `null` = end. */
export interface ActivityPage {
	items: AuditEntry[];
	page_size: number;
	next_cursor: string | null;
}

export interface LoginAttemptItem {
	id: string;
	identifier: string;
	success: boolean;
	reason: string;
	ip: string | null;
	user_agent: string | null;
	/** The app the attempt came from (`CLIENT_APPS`); null = not said (an API client, an older build, before 0065). */
	client_app: ClientApp | null;
	created_at: string;
}

/** `GET /login-attempts`: one row of the sign-in log across every login, with who it resolved to. */
export interface SignInLogItem extends LoginAttemptItem {
	user: { id: string; username: string; name: string } | null;
}

/** `GET /login-attempts`'s `app` filter: an app, or `UNKNOWN` for attempts that did not say. */
export const SIGN_IN_LOG_APPS = [...CLIENT_APPS, "UNKNOWN"] as const;
export type SignInLogApp = (typeof SIGN_IN_LOG_APPS)[number];

/** `GET /login-attempts` (ADMIN, SUPER_ADMIN): the sign-in log across every login, newest first. */
export const signInLogQuery = keysetQuery.extend({
	app: z.enum(SIGN_IN_LOG_APPS).optional(),
	/** `true` = signed in, `false` = refused (wrong password, locked, 2FA asked…). */
	success: z.enum(["true", "false"]).optional(),
});
export type SignInLogQuery = z.infer<typeof signInLogQuery>;
export type SignInLogQueryInput = z.input<typeof signInLogQuery>;

/** `GET /login-attempts` — keyset, newest first; `next_cursor` null on the last page. No total: the log only grows. */
export interface SignInLogPage {
	items: SignInLogItem[];
	next_cursor: string | null;
}

export interface LoginLock {
	locked: boolean;
	failures: number;
	retry_after_seconds: number | null;
}

/** `GET /users/:id/login-attempts` — paginated envelope plus the live limiter state. */
export interface LoginAttemptsPage {
	items: LoginAttemptItem[];
	page: number;
	page_size: number;
	total: number;
	total_pages: number;
	lock: LoginLock;
}

/** One row of `GET /sessions` (SUPER_ADMIN). */
export interface SessionItem {
	sid: string;
	kind: string;
	active_role: RoleCode;
	ip: string | null;
	user_agent: string | null;
	created_at: string;
	last_seen_at: string;
	expires_at: string;
	refresh_count: number;
	recently_active: boolean;
	user: { id: string; username: string; name: string };
}

// ── Activity filters ────────────────────────────────────────────────────────

/**
 * Audited tables grouped into the modules people think in ("what did Leon
 * do in Accreditation?"). The API validates `tables` against the flat list;
 * the web maps a module pick to its tables. Add a table here when a new
 * trigger is attached (see migrations 0004 / 0005 / 0007 / 0008 / 0009 / 0012 / 0015) —
 * a table missing here makes `?tables=<it>` a 422.
 */
export const AUDIT_MODULES = {
	users: {
		label: "Users & access",
		tables: [
			"users", "user_roles", "user_mfa", "user_scopes",
			"user_groups", "user_group_scopes", "user_group_permissions", "user_group_sports",
		],
	},
	people: { label: "People", tables: ["people", "athlete_achievements", "para_athlete_classifications"] },
	accreditation: { label: "Accreditation", tables: ["ac_cards", "print_batches"] },
	competition: {
		label: "Competition",
		tables: [
			"games", "sports", "disciplines", "events", "sport_classes", "event_sport_classes", "venues",
			"event_phases", "meets", "competitors", "competitor_members", "phase_competitors",
			"meet_competitors", "meet_lineups", "meet_officials", "event_medals",
		],
	},
	functions: { label: "Functions", tables: ["main_functions", "sub_functions", "access_rules", "access_rule_codes", "main_function_access_defaults"] },
	reference: {
		label: "Reference data",
		tables: [
			"countries", "responsible_organizations", "affiliated_organizations", "sub_agencies",
			"organization_types", "access_codes", "card_handover_locations",
		],
	},
} as const satisfies Record<string, { label: string; tables: readonly string[] }>;
export type AuditModule = keyof typeof AUDIT_MODULES;

export const AUDIT_TABLES = Object.values(AUDIT_MODULES).flatMap((m) => [...m.tables]) as readonly string[];

export const AUDIT_ACTIONS = ["INSERT", "UPDATE", "DELETE"] as const;

/** Query string for `GET /users/:id/activity` (besides `scope`, `cursor`, `page_size`). */
export interface ActivityFilters {
	/** Comma-separated table names (from AUDIT_TABLES). */
	tables?: string;
	action?: (typeof AUDIT_ACTIONS)[number];
	/** ISO date (YYYY-MM-DD) or full ISO timestamp, inclusive / exclusive. */
	from?: string;
	to?: string;
	/** Free text — matched against the reason and the target account's name / username. */
	q?: string;
}
